import React, { useEffect, useMemo, useState } from "react";
import { Check, Copy, MessageSquareText, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";
import { trackBoth } from "../lib/metaEvent";

const STORE = "hm_closing_checklist";
const SHARE_URL = "https://www.hellomortgage.ca/strategy-vault/what-not-to-do-before-closing/";
const SMS_BODY = `Firm deal? Don't blow it before closing. Hello Mortgage's 7 rules (tick them off as you go): ${SHARE_URL}`;

let viewed = false;  // StrictMode re-runs effects in dev; one ViewContent per visit
const load = () => { try { return JSON.parse(localStorage.getItem(STORE) || "{}") || {}; } catch { return {}; } };

// Tick-box version of the article's seven rules. State lives in the browser so the client can come back to it.
export default function ClosingChecklist({ rules = [] }) {
  const [done, setDone] = useState(load);
  useEffect(() => { if (!viewed) { viewed = true; trackBoth("ViewContent", { content_name: "closing_checklist" }); } }, []);
  useEffect(() => { try { localStorage.setItem(STORE, JSON.stringify(done)); } catch { /* storage blocked */ } }, [done]);
  const count = useMemo(() => rules.filter((r) => done[r.id]).length, [rules, done]);
  const allDone = rules.length > 0 && count === rules.length;

  const toggle = (id) => setDone((d) => ({ ...d, [id]: !d[id] }));
  const copy = async () => { try { await navigator.clipboard.writeText(SHARE_URL); toast.success("Link copied. Send it to anyone with a firm deal."); } catch { toast.error("Couldn’t copy. Long-press the address bar instead."); } };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "7 things not to do before closing", text: SMS_BODY, url: SHARE_URL }); } catch { /* dismissed */ } }
    else copy();
  };

  return (
    <section className="cc-widget" data-testid="closing-checklist" aria-labelledby="cc-title">
      <div className="cc-head">
        <div>
          <span className="tag">Your pre-closing checklist</span>
          <h3 id="cc-title">Tick these off. Stay boring. Get the keys.</h3>
        </div>
        <div className="cc-progress" data-testid="closing-checklist-progress" aria-live="polite">
          <strong>{count}</strong>/{rules.length}
          <span className="cc-bar" aria-hidden="true"><i style={{ width: `${rules.length ? (count / rules.length) * 100 : 0}%` }} /></span>
        </div>
      </div>
      <ol className="cc-list">
        {rules.map((r, i) => (
          <li key={r.id} className={done[r.id] ? "is-done" : ""}>
            <button type="button" className="cc-item" onClick={() => toggle(r.id)} aria-pressed={!!done[r.id]} data-testid={`closing-checklist-item-${r.id}`}>
              <span className="cc-box" aria-hidden="true">{done[r.id] && <Check size={16} strokeWidth={3} />}</span>
              <span className="cc-copy">
                <span className="cc-num">0{i + 1}</span>
                <strong>{r.title}</strong>
                <small>{r.dont}</small>
              </span>
            </button>
          </li>
        ))}
      </ol>
      <p className={`cc-done-line${allDone ? " is-live" : ""}`} data-testid="closing-checklist-done-line" aria-live="polite">
        {allDone ? "Seven for seven. You are officially the most boring borrower in Alberta, and your lawyer loves you for it." : "Not sure about one? Text us before you do it. Asking is free."}
      </p>
      <div className="cc-actions">
        <a className="btn outline" href={`sms:?&body=${encodeURIComponent(SMS_BODY)}`} data-testid="closing-checklist-sms"><MessageSquareText size={16} aria-hidden="true" /> Text it to yourself</a>
        <button type="button" className="btn outline" onClick={share} data-testid="closing-checklist-share"><Share2 size={16} aria-hidden="true" /> Share</button>
        <button type="button" className="btn outline" onClick={copy} data-testid="closing-checklist-copy"><Copy size={16} aria-hidden="true" /> Copy link</button>
        <button type="button" className="btn outline cc-print" onClick={() => window.print()} data-testid="closing-checklist-print"><Printer size={16} aria-hidden="true" /> Print</button>
      </div>
    </section>
  );
}
